import clsx from "clsx";
import { type FC, ReactNode } from "react";

import { SectionUserOptionsMenu } from "./SectionUserOptionsMenu";
import { userActivityIcons } from "./helpers";

import { t } from "i18n/i18n";
import { ListItem } from "mds/components/ListItem";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import {
  HandRaiseCircleIcon,
  PresentPlayFilledIcon,
  PresentPlayIcon,
  ReactionVoteNoIcon,
  ReactionVoteYesIcon,
  ScreenshareFilledIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  SectionUserActivityStatus,
  selectActivityStatusForUser,
  selectClassUserByUserId,
  selectFollowingStatusForUser,
  selectSectionUserByUserId,
  userSelectors,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { REACTION_TO_ICON } from "utils/reactions";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

type EnhancedSectionUserActivityStatus =
  | SectionUserActivityStatus
  | "disabled"
  | "following"
  | "presenting"
  | "screensharing";

const enhancedUserActivityIcons: Record<EnhancedSectionUserActivityStatus, ReactNode> = {
  ...userActivityIcons,
  following: <PresentPlayIcon />,
  presenting: (
    <span className="text-green">
      <PresentPlayFilledIcon />
    </span>
  ),
  screensharing: <ScreenshareFilledIcon className="text-green" size="m" />,
};

const statusTooltipText: Record<EnhancedSectionUserActivityStatus, string> = {
  active: "",
  inactive: "",
  offline: "",
  disabled: "",
  following: t("presentation.following"),
  presenting: t("presentation.presenting"),
  screensharing: t("av.screenshare.banner.screensharing"),
};

interface SectionUserItemProps {
  userId: string;
  presentingUserId?: string | null;
  screensharingUserId?: string | null;
}

export const SectionUserItem: FC<SectionUserItemProps> = ({
  userId,
  presentingUserId,
  screensharingUserId,
}) => {
  const { canAuthorSection, canEditEnrollment } = useCapabilities();
  const user = useObjectSelector(
    (state) => userSelectors.selectById(state, userId),
    PROFILE_PROPERTIES,
  );
  const sectionActivityStatus = useAppSelector((state) =>
    selectActivityStatusForUser(state, userId),
  );
  const isFollowing = useAppSelector((state) => selectFollowingStatusForUser(state, userId));
  const sectionUser = useAppSelector((state) => selectSectionUserByUserId(state, userId));
  const classUser = useAppSelector((state) => selectClassUserByUserId(state, userId));

  if (!user || !sectionUser) return null;

  const isPresenting = userId === presentingUserId;
  const isScreensharing = userId === screensharingUserId;
  const showSectionUserOptionMenu = canAuthorSection || canEditEnrollment;
  const status = sectionUser.is_disabled
    ? "disabled"
    : isScreensharing
      ? "screensharing"
      : isPresenting
        ? "presenting"
        : isFollowing
          ? "following"
          : sectionActivityStatus;

  const ReactionIcon = classUser?.reaction && REACTION_TO_ICON[classUser.reaction];

  return (
    <ListItem
      className={clsx("flex items-center justify-between gap-2 text-sm", {
        offline:
          sectionActivityStatus === SectionUserActivityStatus.OFFLINE && !sectionUser.is_disabled,
      })}
      key={userId}
    >
      <div className="flex min-w-0 flex-row items-center gap-2" title={user.email}>
        {ReactionIcon ? (
          <ReactionIcon />
        ) : (
          <TooltipTrigger content={statusTooltipText[status]}>
            {enhancedUserActivityIcons[status]}
          </TooltipTrigger>
        )}
        <span className={clsx("truncate", { "text-black-tint-55": sectionUser.is_disabled })}>
          {getUserFullName(user)}
        </span>
      </div>
      <div className="flex flex-row items-center gap-1">
        {classUser?.hand_raised_at && (
          <HandRaiseCircleIcon aria-label={t("av.raised_hand")} className="text-blue" />
        )}
        {classUser?.vote === "yes" && <ReactionVoteYesIcon aria-label={t("reactions.vote_yes")} />}
        {classUser?.vote === "no" && <ReactionVoteNoIcon aria-label={t("reactions.vote_no")} />}

        {showSectionUserOptionMenu && <SectionUserOptionsMenu userId={userId} />}
      </div>
    </ListItem>
  );
};
